import { Box } from "@mui/material";
import {
    WiDaySunny,
    WiCloudy,
    WiRain,
    WiThermometer,
    WiMoonAltWaningGibbous4,
    WiStrongWind
} from "react-icons/wi";

interface Weather {
    forecast_date: string;
    temp_min: number;
    temp_max: number;
    feels_like_c: number;
    humidity: number;
    chance_rain: number;
    cloud_cover: number;
    wind_speed_kph: number;
    wind_dir: number;
    pressure_mb: number;
    visibility_km: number;
    moon_phase: number;
    sunrise: string;
    sunset: string;
    condition: string;
    moon_phase_emoji: string;
    moon_phase_name: string;
    moon_phase_percent: number;
    weather_emoji: string;
    wind_direction: string;
    wind_direction_emoji: string;
}

interface WeatherDisplayProps {
    weather: Weather;
    title?: string;
    description?: string;
}

const WeatherDisplay = ({ weather, title, description }: WeatherDisplayProps) => {
    // Get weather icon based on condition
    const getWeatherIcon = () => {
        return weather?.weather_emoji;
    };

    // Format date
    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { weekday: 'long', month: '2-digit', day: '2-digit', year: 'numeric' });
    };

    // Format time (HH:MM:SS to HH:MM:SS)
    const formatTime = (timeStr: string) => {
        return timeStr; // Already in HH:MM:SS format
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Title and Description */}
            {(title || description) && (
                <Box sx={{ mb: 3 }}>
                    {title && (
                        <Box sx={{
                            fontSize: '20px',
                            fontWeight: 600,
                            color: '#333',
                            mb: 1.5
                        }}>
                            {title}
                        </Box>
                    )}
                    {description && (
                        <Box sx={{
                            fontSize: '14px',
                            color: '#666',
                            lineHeight: 1.6
                        }}>
                            {description}
                        </Box>
                    )}
                </Box>
            )}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 2,
                mb: 2
            }}>
                {/* Main Weather Card */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #187A85 0%, #1a8a96 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: '200px'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box sx={{ fontSize: '60px' }}>
                            {getWeatherIcon()}
                        </Box>
                    </Box>
                    <Box>
                        <Box sx={{ fontSize: '48px', fontWeight: 700, lineHeight: 1 }}>
                            {weather.temp_max.toFixed(2)}
                        </Box>
                        <Box sx={{ fontSize: '18px', mt: 1, opacity: 0.9 }}>
                            {weather.condition}
                        </Box>
                        <Box sx={{ fontSize: '14px', mt: 1, opacity: 0.8 }}>
                            {formatDate(weather.forecast_date)}
                        </Box>
                    </Box>
                </Box>

                {/* Temperature Card */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #E8F5F7 0%, #D4EEF2 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px'
                }}>
                    <WiThermometer size={80} color="#187A85" />
                    <Box sx={{ fontSize: '48px', fontWeight: 700, color: '#187A85', mt: 2 }}>
                        {weather.temp_max.toFixed(0)}°
                    </Box>
                    <Box sx={{ fontSize: '16px', color: '#666', mt: 1 }}>
                        Temperature
                    </Box>
                </Box>

                {/* Moon Phase Card */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #2C5F6F 0%, #1a4a57 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ fontSize: '60px' }}>{weather?.moon_phase_emoji}</Box>
                    </Box>
                    <Box sx={{ fontSize: '24px', fontWeight: 700, mt: 2, textAlign: 'center' }}>
                        {weather.moon_phase_name || `${(weather.moon_phase * 100).toFixed(0)}%`}
                    </Box>
                    <Box sx={{ fontSize: '14px', mt: 1, opacity: 0.8 }}>
                        {weather.moon_phase_percent}% illumination
                    </Box>
                    <Box sx={{ fontSize: '16px', mt: 1, opacity: 0.9 }}>
                        Moon Phase
                    </Box>
                </Box>

                {/* Wind Card */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #C8F0F5 0%, #A8E5ED 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WiStrongWind size={60} color="#187A85" />
                        <Box sx={{ fontSize: '32px' }}>{weather.wind_direction_emoji}</Box>
                    </Box>
                    <Box sx={{ fontSize: '48px', fontWeight: 700, color: '#187A85', mt: 1 }}>
                        {weather.wind_speed_kph.toFixed(1)}
                    </Box>
                    <Box sx={{ fontSize: '16px', color: '#666', mt: 0.5 }}>
                        kph {weather.wind_direction}
                    </Box>
                    <Box sx={{ fontSize: '16px', color: '#666' }}>
                        Wind
                    </Box>
                </Box>
            </Box>

            {/* Sunrise and Sunset Cards */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2
            }}>
                {/* Sunrise Card */}
                <Box sx={{
                    background: '#FADDBE',
                    borderRadius: '16px',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: '180px'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ fontSize: '18px', color: '#333', mt: 1 }}>Sunrise</Box>
                        <Box sx={{ width: 80, height: 80 }}>
                            <img src="/assets/svg/sunrize.svg" alt="Sunrise" style={{ width: '100%', height: '100%' }} />
                        </Box>
                    </Box>
                    <Box sx={{ fontSize: '42px', fontWeight: 700, color: '#0E7490' }}>
                        {formatTime(weather.sunrise)}
                    </Box>
                </Box>

                {/* Sunset Card */}
                <Box sx={{
                    background: '#E5E7EB',
                    borderRadius: '16px',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: '180px'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ fontSize: '18px', color: '#333', mt: 1 }}>Sunset</Box>
                        <Box sx={{ width: 80, height: 80 }}>
                            <img src="/assets/svg/suset.svg" alt="Sunset" style={{ width: '100%', height: '100%' }} />
                        </Box>
                    </Box>
                    <Box sx={{ fontSize: '42px', fontWeight: 700, color: '#1F2937' }}>
                        {formatTime(weather.sunset)}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default WeatherDisplay;
